import { useEffect, useState } from 'preact/hooks';
import type { CurrentUser } from './types';

/**
 * Identity probe. The unified SPA is single-origin (ADR-0001) so /me lives at
 * the same host - no cross-subdomain CORS dance. The session cookie travels
 * naturally with `credentials: 'include'`.
 *
 * Three terminal states:
 *   - authenticated: 200 with user JSON
 *   - anonymous: 401
 *   - error: network failure or non-401 non-200 (e.g. 500)
 *
 * Error is treated like anonymous by the navbar's sign-in affordance - the
 * most useful action is "sign in", and exposing transient HTTP errors in the
 * chrome would be noise.
 */
export type MeState =
    | { status: 'loading' }
    | { status: 'authenticated'; user: CurrentUser }
    | { status: 'anonymous'; user?: CurrentUser }
    | { status: 'error'; error: Error };

// ---- Shared module-level cache --------------------------------------------
//
// Per-card components (WorkCard mounts a CollectionsButton + a like heart,
// both of which call useMe()) previously fired one /me fetch each. A 30-card
// catalog page produced 60+ identical requests on first paint. The singleton
// below collapses them to one shared fetch with pub/sub state updates.
//
// Sign-in and sign-out go through window.location navigation, which reloads
// the SPA and resets module state - no manual invalidation needed here.

type Listener = (state: MeState) => void;

let cached: MeState = { status: 'loading' };
let inflight: Promise<void> | null = null;
const listeners = new Set<Listener>();

function publish(next: MeState): void {
    cached = next;
    listeners.forEach((l) => l(next));
}

function fetchMe(): Promise<void> {
    if (inflight) return inflight;
    inflight = fetch('/me', { credentials: 'include' })
        .then(async (res) => {
            if (!res.ok) {
                if (res.status === 401) publish({ status: 'anonymous' });
                else publish({ status: 'error', error: new Error(`HTTP ${res.status}`) });
                return;
            }
            const user = (await res.json()) as CurrentUser;
            publish({ status: 'authenticated', user });
        })
        .catch((err: unknown) => {
            publish({
                status: 'error',
                error: err instanceof Error ? err : new Error(String(err)),
            });
        })
        .finally(() => {
            inflight = null;
        });
    return inflight;
}

export function useMe(): MeState {
    const [state, setState] = useState<MeState>(cached);

    useEffect(() => {
        listeners.add(setState);
        // Seed the cache on first subscriber; subsequent mounts see the
        // resolved state immediately without re-fetching.
        if (cached.status === 'loading') void fetchMe();
        // Keep local state in sync with the cache when we subscribe - covers
        // the race where the fetch resolved between the useState initialiser
        // running and the effect attaching the listener.
        else if (state !== cached) setState(cached);
        return () => {
            listeners.delete(setState);
        };
    }, []);

    return state;
}

export function isAdmin(user?: CurrentUser): boolean {
    return !!user?.roles?.some((r) => r.toLowerCase() === 'admin');
}

/**
 * End the session and return the user to `returnUrl` (defaults to `/`).
 * Issues a navigation rather than a fetch so the cookie clearing and the
 * redirect happen in the same request.
 */
export function signOut(returnUrl: string = '/'): void {
    if (typeof window === 'undefined') return;
    const url = `/auth/logout?return=${encodeURIComponent(returnUrl)}`;
    window.location.assign(url);
}

/**
 * Path to the sign-in page with a `?return=` back to where the user is now
 * (defaults to the current path + query). The sign-in page carries it through
 * the OAuth handshake, so a signed-out caller who follows this link lands back
 * on the same page. Used by the anonymous Like / Collect nudges.
 */
export function signInPath(returnUrl?: string): string {
    const dest =
        returnUrl ??
        (typeof window !== 'undefined'
            ? window.location.pathname + window.location.search
            : '/');
    return `/signin?return=${encodeURIComponent(dest)}`;
}

/**
 * Build the URL that starts the OAuth handshake for a given provider, with
 * a `?return=` set to the current location so the cookie issuer can bounce
 * the user back where they were.
 */
export function challengeUrl(provider: string, returnUrl?: string): string {
    const dest = returnUrl ?? (typeof window !== 'undefined' ? window.location.href : '/');
    return `/auth/${encodeURIComponent(provider)}/challenge?return=${encodeURIComponent(dest)}`;
}
